import { FormikProps } from "formik"
import { useRef, useState } from "react"
import { MERCHANT_ADMIN_PRICE_LISTS_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { CreateOrEditPriceListFormValues } from "../priceListTypes"
import { CreateUpdatePriceListForm } from "../components/form/CreateUpdatePriceListForm"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { createPriceList, PriceListQuery } from "../priceListApi"
import { useNavigate } from "react-router"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { mapDataToCreateOrEdit } from "../helpers"

export function CreatePriceList() {
    const navigate = useNavigate()
    const priceListFormRef = useRef<FormikProps<CreateOrEditPriceListFormValues>>(null)
    const { merchantId } = useMerchant()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSaveClick = async () => {
        setIsSubmitting(true)
        try {
            await priceListFormRef.current?.submitForm()
        } finally {
            setIsSubmitting(false)
        }
    }

    const { mutateAsync: callCreatePriceList } = useApiMutation(createPriceList, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
            navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE)
        },
    })

    const createNewPriceList = async (data: CreateOrEditPriceListFormValues) => {
        const mappedData = mapDataToCreateOrEdit(data, merchantId)
        await callCreatePriceList(mappedData)
    }

    return (
        <>
            <LocalizedStrict id="create-price-list-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRICE_LIST}
                    title="Create Price List"
                    rightElement={
                        <LocalizedStrict id="create-price-list-create-button">
                            <StandardLoadingButton loading={isSubmitting} onClick={handleSaveClick}>
                                Create
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-price-lists">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRICE_LISTS_ROUTE}>Price Lists</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-price-list-create">
                            <Breadcrumb>Create</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <CreateUpdatePriceListForm ref={priceListFormRef} onSubmit={createNewPriceList} />
        </>
    )
}
